import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { SeasonPill } from "@/components/SeasonCountdown";
import { TEST_IDS } from "@/lib/testIds";
import { AvatarIcon } from "@/lib/identity";
import { PremiumBadge } from "@/components/PremiumBadge";
import SignalsBell from "@/components/SignalsBell";
import {
  Power, House, GameController, Broadcast, Stairs, Sword, Fire,
  User as UserIcon, Crown, Medal, Star, Chat, ShieldStar,
  CaretRight, CaretDown, ClockCounterClockwise,
} from "@phosphor-icons/react";

const NAV_GROUPS = [
  {
    key: "play",
    title: "Play",
    icon: GameController,
    items: [
      { to: "/daily", label: "NK Daily", testid: "nav-daily", Icon: Broadcast,
        desc: "Twice a day. One live show." },
      { to: "/ladder", label: "NK Ladder", testid: TEST_IDS.navLadder, Icon: Stairs,
        desc: "Climb 15 rungs. Cash out or push." },
      { to: "/duel", label: "NK Duel", testid: "nav-duel", Icon: Sword,
        desc: "1-v-1 head-to-head." },
      { to: "/survival", label: "NK Survival", testid: "nav-survival", Icon: Fire,
        desc: "Endless. One miss, you're out." },
      { to: "/saturday", label: "Saturday", testid: "nav-saturday", Icon: Star, gold: true,
        desc: "The weekly big-stakes show." },
      { to: "/championship", label: "Championship", testid: "nav-championship", Icon: Crown, gold: true,
        desc: "Day 31. Pan-African finals." },
    ],
  },
  {
    key: "ranks",
    title: "Ranks",
    icon: Crown,
    items: [
      { to: "/leaderboard", label: "Season Leaderboard", testid: TEST_IDS.navLeaderboard, Icon: Crown,
        desc: "Live standings, resets every 30 days." },
      { to: "/history", label: "My History", testid: "nav-history", Icon: ClockCounterClockwise,
        desc: "Every game you've played." },
      { to: "/hall-of-fame", label: "Hall of Fame", testid: "nav-hall-of-fame", Icon: Medal,
        desc: "Past champions." },
    ],
  },
];

// Mobile bottom tabs (Home + 4 groups)
const MOBILE_TABS = [
  { to: "/home",    label: "Home",  testid: TEST_IDS.navHome, Icon: House },
  { to: "/play",    label: "Play",  testid: "mobile-nav-play", Icon: GameController },
  { to: "/ranks",   label: "Ranks", testid: "mobile-nav-ranks", Icon: Crown },
  { to: "/chat",    label: "Chat",  testid: "mobile-nav-chat", Icon: Chat },
  { to: "/profile", label: "You",   testid: "mobile-nav-profile", Icon: UserIcon },
];

function findActiveGroupKey(pathname) {
  for (const g of NAV_GROUPS) {
    if (g.items.some((it) => pathname.startsWith(it.to))) return g.key;
  }
  return null;
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const displayName = user?.username || user?.name || "";
  const isAdmin = user?.role === "super_admin" || user?.role === "sub_admin" || user?.is_admin;

  // Desktop collapsible groups — auto-expand the one containing the active route
  const activeGroup = findActiveGroupKey(location.pathname);
  const [openGroups, setOpenGroups] = useState(() => new Set(activeGroup ? [activeGroup] : []));
  useEffect(() => {
    if (activeGroup) {
      setOpenGroups((prev) => prev.has(activeGroup) ? prev : new Set([...prev, activeGroup]));
    }
  }, [activeGroup]);
  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <div className="nk-arena min-h-screen text-nk-text font-sans relative">
      <div className="relative z-10">
      <header
        data-testid={TEST_IDS.header}
        className="sticky top-0 z-30 border-b border-white/5 bg-nk-charcoal/70 backdrop-blur-xl"
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-2 sm:gap-4">
          <button onClick={() => navigate("/home")} className="flex items-center gap-2 sm:gap-3 group min-w-0">
            <div
              className="w-8 h-8 rounded-sm flex items-center justify-center shrink-0 transition-all group-hover:shadow-[0_0_0_2px_rgba(212,175,55,0.25)]"
              style={{ background: "linear-gradient(135deg, #0B3D2E 0%, #13593F 100%)", border: "1px solid rgba(212,175,55,0.6)" }}
            >
              <span className="font-display font-bold text-nk-gold text-sm">N</span>
            </div>
            <span className="font-display text-lg sm:text-xl font-medium tracking-tight text-white group-hover:text-nk-gold transition-colors truncate">
              NKognito
            </span>
          </button>

          <div className="hidden md:block"><SeasonPill /></div>

          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <SignalsBell />
            <button
              onClick={() => navigate("/profile")}
              className="flex items-center gap-2 px-2 sm:px-3 py-1.5 border border-nk-line rounded-sm hover:border-nk-lineHi transition-colors max-w-[140px] sm:max-w-none"
            >
              <AvatarIcon id={user?.avatar} size={20} />
              <span className="text-xs sm:text-sm truncate">{displayName}</span>
              <PremiumBadge active={user?.nk_plus_active} size="sm" className="shrink-0" />
              {isAdmin && <span className="hidden sm:inline text-[10px] text-nk-gold ml-1 font-mono shrink-0">admin</span>}
            </button>
            <button
              data-testid={TEST_IDS.logoutBtn}
              onClick={logout}
              className="p-2 border border-nk-line rounded-sm hover:border-nk-bad hover:text-nk-bad transition-colors shrink-0"
              title="Sign out"
              aria-label="Sign out"
            >
              <Power size={14} weight="bold" />
            </button>
          </div>
        </div>

        {/* Mobile season pill row */}
        <div className="md:hidden border-t border-nk-line/40 px-4 py-2 flex justify-center">
          <SeasonPill />
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-24 lg:pb-16 grid grid-cols-12 gap-6 lg:gap-8">
        {/* Desktop grouped sidebar nav (collapsible) */}
        <aside className="hidden lg:block lg:col-span-2">
          <div className="lg:sticky lg:top-24">
            <p className="font-mono text-[10px] text-nk-dim uppercase tracking-[0.18em] px-3 mb-3">
              The Arena
            </p>
            <nav className="flex flex-col gap-1" aria-label="Primary">
              <NavLink
                to="/home"
                data-testid={TEST_IDS.navHome}
                className={({ isActive }) =>
                  `text-sm px-3 py-2 rounded-sm flex items-center gap-2.5 transition-all relative ${
                    isActive
                      ? "bg-nk-green/40 text-white border-l-2 border-nk-gold"
                      : "text-white/60 hover:text-white border-l-2 border-transparent"
                  }`
                }
              >
                <House size={15} weight="duotone" />
                Home
              </NavLink>

              <NavLink
                to="/chat"
                data-testid="nav-chat"
                className={({ isActive }) =>
                  `text-sm px-3 py-2 rounded-sm flex items-center gap-2.5 transition-all relative ${
                    isActive
                      ? "bg-nk-green/40 text-white border-l-2 border-nk-gold"
                      : "text-white/60 hover:text-white border-l-2 border-transparent"
                  }`
                }
              >
                <Chat size={15} weight="duotone" />
                Chat
              </NavLink>

              {NAV_GROUPS.map((group) => {
                const isOpen = openGroups.has(group.key);
                const hasActive = activeGroup === group.key;
                return (
                  <div key={group.key} className="mt-1">
                    <button
                      onClick={() => toggleGroup(group.key)}
                      data-testid={`nav-group-${group.key}`}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] transition-colors ${
                        hasActive ? "text-nk-text" : "text-nk-dim hover:text-nk-mute"
                      }`}
                      aria-expanded={isOpen}
                    >
                      <group.icon size={11} weight="duotone" className={hasActive ? "text-nk-accent" : ""} />
                      <span className="flex-1 text-left">{group.title}</span>
                      {isOpen ? <CaretDown size={9} /> : <CaretRight size={9} />}
                    </button>
                    {isOpen && (
                      <div className="flex flex-col gap-0.5 mt-0.5">
                        {group.items.map(({ to, label, testid, Icon, gold }) => (
                          <NavLink
                            key={to}
                            to={to}
                            data-testid={testid}
                            className={({ isActive }) =>
                              `text-sm px-3 py-1.5 rounded-sm flex items-center gap-2.5 transition-all ${
                                isActive
                                  ? `bg-nk-green/40 ${gold ? "text-nk-gold border-l-2 border-nk-gold" : "text-white border-l-2 border-nk-gold"}`
                                  : `${gold ? "text-nk-gold/80 hover:text-nk-gold" : "text-white/60 hover:text-white"} border-l-2 border-transparent`
                              }`
                            }
                          >
                            <Icon size={14} weight={gold ? "fill" : "duotone"} />
                            {label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              <NavLink
                to="/profile"
                data-testid="nav-profile"
                className={({ isActive }) =>
                  `text-sm px-3 py-2 rounded-sm flex items-center gap-2.5 transition-all relative ${
                    isActive
                      ? "bg-nk-green/40 text-white border-l-2 border-nk-gold"
                      : "text-white/60 hover:text-white border-l-2 border-transparent"
                  }`
                }
              >
                <UserIcon size={15} weight="duotone" />
                You
              </NavLink>

              {isAdmin && (
                <div className="mt-3 pt-3 border-t border-nk-line/40">
                  <p className="text-[10px] text-nk-dim uppercase tracking-[0.22em] px-3 mb-1.5">Staff</p>
                  <NavLink
                    to="/admin"
                    data-testid={TEST_IDS.navAdmin}
                    className={({ isActive }) =>
                      `text-sm px-3 py-2 rounded-sm flex items-center gap-2.5 transition-all ${
                        isActive
                          ? "bg-nk-surface text-nk-gold border-l-2 border-nk-gold"
                          : "text-nk-gold/80 hover:text-nk-gold border-l-2 border-transparent"
                      }`
                    }
                  >
                    <ShieldStar size={15} weight="fill" />
                    Command Center
                  </NavLink>
                </div>
              )}
            </nav>
          </div>
        </aside>

        <main className="col-span-12 lg:col-span-10 min-w-0 animate-fade-in">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom tab bar — 5 direct links, no sheets */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-nk-charcoal/95 backdrop-blur-xl border-t border-white/10"
        aria-label="Primary mobile"
      >
        <div className="flex items-stretch justify-around max-w-[600px] mx-auto px-1 py-1"
             style={{ paddingBottom: "max(4px, env(safe-area-inset-bottom))" }}>
          {MOBILE_TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={t.testid}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-md transition-colors relative min-w-0 ${
                  isActive ? "text-white bg-nk-green/45" : "text-white/55 active:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute top-0.5 w-1 h-1 rounded-full bg-nk-gold" aria-hidden="true" />}
                  <t.Icon size={18} weight={isActive ? "fill" : "duotone"} />
                  <span className="text-[10px] font-medium tracking-wide leading-none">{t.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
      </div>
    </div>
  );
}
